/**
 * geluid-bewerk.js — een microfoonopname klaarmaken als scangeluid (in de browser).
 *
 * Een opname van "test" klonk na afloop vrijwel stil. Oorzaken: een microfoon die na het starten
 * nog even opwarmt, ruisonderdrukking van de browser die een kort woord deels wegfiltert, en
 * stilte voor en na het woord. Een scangeluid moet direct en duidelijk klinken. Daarom:
 *   - stilte aan begin en eind afknippen (met een klein randje, anders klinkt het afgehakt);
 *   - op volle sterkte zetten (piek op ~90%);
 *   - opslaan als wav: dat speelt elke telefoon af en past ruim binnen 600 KB (3 s mono ~260 KB).
 * Is er niets boven de ruis te horen, dan is de opname leeg en zeggen we dat.
 */

/** Start- en eindindex van het hoorbare deel, of null als alles stil is. */
export function vindGeluid(samples, sampleRate, { drempel = 0.02, randMs = 40 } = {}) {
  let piek = 0;
  for (let i = 0; i < samples.length; i++) piek = Math.max(piek, Math.abs(samples[i]));
  if (piek < drempel) return null;
  // Relatief aan de piek, met een ondergrens: zo telt het ruisniveau van de kamer niet als geluid.
  const grens = Math.max(drempel, piek * 0.08);
  let begin = 0;
  while (begin < samples.length && Math.abs(samples[begin]) < grens) begin++;
  let eind = samples.length - 1;
  while (eind > begin && Math.abs(samples[eind]) < grens) eind--;
  const rand = Math.round((randMs / 1000) * sampleRate);
  return { begin: Math.max(0, begin - rand), eind: Math.min(samples.length, eind + 1 + rand), piek };
}

/** Bijknippen en op sterkte brengen. Geeft een nieuwe Float32Array, of null als hij stil is. */
export function knipEnVersterk(samples, sampleRate, opties) {
  const g = vindGeluid(samples, sampleRate, opties);
  if (!g) return null;
  const uit = samples.slice(g.begin, g.eind);
  const factor = Math.min(20, 0.9 / g.piek); // hooguit 20x: anders blaas je alleen ruis op
  const fade = Math.min(uit.length >> 1, Math.round(0.005 * sampleRate)); // 5 ms in/uit: geen tik
  for (let i = 0; i < uit.length; i++) {
    let v = uit[i] * factor;
    if (i < fade) v *= i / fade;
    if (i >= uit.length - fade) v *= (uit.length - 1 - i) / fade;
    uit[i] = Math.max(-1, Math.min(1, v));
  }
  return uit;
}

/** Mono 16-bit PCM wav. */
export function maakWav(samples, sampleRate) {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buf);
  const tekst = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  tekst(0, 'RIFF'); v.setUint32(4, 36 + samples.length * 2, true); tekst(8, 'WAVE');
  tekst(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, sampleRate, true); v.setUint32(28, sampleRate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  tekst(36, 'data'); v.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) v.setInt16(44 + i * 2, Math.round(samples[i] * 0x7fff), true);
  return new Blob([buf], { type: 'audio/wav' });
}

/**
 * Van een MediaRecorder-opname een scangeluid maken: { file, duurMs } of { leeg: true }.
 * Lukt het decoderen niet, dan null: dan gebruikt de aanroeper de opname zoals hij is.
 */
export async function maakScangeluidVanOpname(blob, naam = 'opname') {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  const ctx = new Ctx();
  try {
    const audio = await ctx.decodeAudioData(await blob.arrayBuffer());
    // Mono: kanalen middelen.
    const mono = new Float32Array(audio.length);
    for (let k = 0; k < audio.numberOfChannels; k++) {
      const d = audio.getChannelData(k);
      for (let i = 0; i < d.length; i++) mono[i] += d[i] / audio.numberOfChannels;
    }
    const bewerkt = knipEnVersterk(mono, audio.sampleRate);
    if (!bewerkt) return { leeg: true };
    const file = new File([maakWav(bewerkt, audio.sampleRate)], `${naam}.wav`, { type: 'audio/wav' });
    return { file, duurMs: Math.round((bewerkt.length / audio.sampleRate) * 1000) };
  } catch (_) {
    return null;
  } finally {
    ctx.close?.();
  }
}
